// @flow

import * as React from 'react';
import { Box, useResponsive } from 'react-system';
import { useResizeRect, useClientPositionRect } from '@realadvisor/observe';
import { graphql, useFragment } from 'react-relay';
import { useTheme } from '../../theme';
import type { MappingImage_products$key } from './__generated__/MappingImage_products.graphql';
import type { MappingImage_image$key } from './__generated__/MappingImage_image.graphql';

const SVG_SIZE = 1000;
const HOVER_CARD_WIDTH = 350;

// svg viewbox always 1000px width
export const getViewbox = (x: number, y: number): $ReadOnlyArray<number> => [
  SVG_SIZE,
  y * (SVG_SIZE / x),
];

type Props = {|
  products: MappingImage_products$key,
  image: MappingImage_image$key,
  hoverIndex?: ?number,
  onProductHover?: (index: ?number) => void,
  onProductClick?: (index: number) => void,
  hoverCard?: (hoverIndex: number) => React.Node,
|};

const Polygon = ({ highlighted, status, ...rest }) => {
  const { colors } = useTheme();
  const getFillColor = status => {
    switch (status) {
      case 'won':
      case 'lost_sold_someone':
      case 'lost_sold_owner':
        return colors.red[500];
      case 'reserved':
        return colors.orange[500];
      case 'available':
        return colors.green[500];
    }
  };

  return (
    <polygon
      strokeWidth={2}
      stroke={colors.white}
      fill={highlighted === false ? 'none' : getFillColor(status)}
      fillOpacity={0.2}
      {...rest}
    />
  );
};

const getHoverCardStyle = (containerRect, containerPos, cursorPosition) => {
  if (containerRect == null || containerPos == null) {
    return {};
  }

  const x = cursorPosition.x - containerPos.x;
  const y = cursorPosition.y - containerPos.y;
  const width = HOVER_CARD_WIDTH;

  const left = Math.max(
    20,
    Math.min(
      containerRect.width - HOVER_CARD_WIDTH - 20,
      x - HOVER_CARD_WIDTH / 2,
    ),
  );

  if (cursorPosition.aboveMiddle) {
    return { width, left, top: y + 26 };
  } else {
    return { width, left, bottom: containerRect.height - y + 16 };
  }
};

// increase depth in svg way to prevent hovering sibling mappings
const moveDown = index => array => {
  if (index == null || index === -1) {
    return array;
  }
  return [
    ...array.slice(0, index),
    ...array.slice(index + 1),
    ...array.slice(index, index + 1),
  ];
};

export const MappingImage = (props: Props): React.Node => {
  const products = useFragment(
    graphql`
      fragment MappingImage_products on Product @relay(plural: true) {
        id
        status
        mapping {
          svgPath
        }
      }
    `,
    props.products,
  );
  const image = useFragment(
    graphql`
      fragment MappingImage_image on Image {
        id
        url
      }
    `,
    props.image,
  );

  const responsive = useResponsive();
  const [showHoverCard, setShowHoverCard] = React.useState(false);
  const [cursorPosition, setCursorPosition] = React.useState({
    x: 0,
    y: 0,
    aboveMiddle: false,
  });
  const imageRef = React.useRef(null);
  const containerRect = useResizeRect(imageRef);
  const containerPos = useClientPositionRect(imageRef);
  const { onProductHover, onProductClick, hoverIndex, hoverCard } = props;

  return (
    <Box
      css={{ position: 'relative' }}
      onMouseMove={e => {
        const windowHeight = document.body?.clientHeight ?? 0;
        setCursorPosition({
          x: e.clientX,
          y: e.clientY,
          aboveMiddle: windowHeight / 2 > e.clientY,
        });
      }}
    >
      <Box css={{ position: 'relative', lineHeight: 0 }}>
        <img
          src={image.url}
          ref={imageRef}
          css={{ width: '100%', display: 'block' }}
        />
        {containerRect != null && (
          <svg
            css={{
              height: '100%',
              width: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1,
            }}
            viewBox={`0 0 ${getViewbox(
              containerRect.width,
              containerRect.height,
            ).join(' ')}`}
          >
            {moveDown(hoverIndex)(
              (products || []).map(
                (product, index) =>
                  product.mapping != null && (
                    <Polygon
                      highlighted={
                        index === hoverIndex || products.length === 1
                      }
                      key={product.id}
                      status={product.status}
                      points={product.mapping.svgPath ?? ''}
                      onMouseEnter={() => {
                        if (onProductHover != null) {
                          onProductHover(index);
                        }
                        setShowHoverCard(true);
                      }}
                      onMouseLeave={() => {
                        if (onProductHover != null) {
                          onProductHover(null);
                        }
                        setShowHoverCard(false);
                      }}
                      onClick={() => {
                        if (onProductClick != null) {
                          onProductClick(index);
                        }
                      }}
                      style={
                        onProductClick != null
                          ? {
                              cursor: 'pointer',
                              pointerEvents: 'visible',
                            }
                          : null
                      }
                    />
                  ),
              ),
            )}
          </svg>
        )}
      </Box>

      {responsive([
        false,
        <Box
          key="desktop"
          css={{
            position: 'absolute',
            bottom: 120,
            zIndex: 2,

            // Sometimes the card doesn't move fast enough.
            // As a result cursor may hover it for a split second.
            // When that happens onMouseLeave fiers on Polygon.
            // But when card finally moves, onMouseEnter does not fire.
            // We end up with cursor hovering a polygon, but showHoverCard === false.
            // We fix this by disabling pointerEvents on the card.
            pointerEvents: 'none',
          }}
          style={getHoverCardStyle(containerRect, containerPos, cursorPosition)}
        >
          {hoverIndex != null &&
            showHoverCard === true &&
            hoverCard != null &&
            hoverCard(hoverIndex)}
        </Box>,
      ])}
    </Box>
  );
};
